// CADENCE-PAGE-01A
// Página especializada de investigación de rupturas de cadencia.
// Reutiliza get_pricing_cadence_attention_v2 y la resolución occurrence-aware.

import { useEffect, useState } from 'react';
import {
  useQuery,
} from '@tanstack/react-query';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { backLabel } from '../../pricingJourney/navigation';


import { pageFrameSx } from '../../pricingJourney/components/PageHeader';

import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import CustomerRelationshipWorkspace
  from '../components/CustomerRelationshipWorkspace';
import MarginCenterSpecializedPageHeader
  from '../components/MarginCenterSpecializedPageHeader';

import {
  getMarginCenterCadenceAttention,
} from '../services/marginCenterService';
// PRC-048: el panel común «Gestión del aviso» sustituye al formulario de archivo.
import CadenceManagementDrawer, { type CadenceManagementTarget } from '../components/cadenceManagement/CadenceManagementDrawer';
import { cadenceDecisionAction } from '../components/cadenceManagement/cadenceDecisionAction';
import { cadenceRowKey, useDeferredCadenceActions, type CadenceListTab } from '../components/cadenceManagement/useDeferredCadenceActions';

import type {
  MarginCenterCadenceAttentionItem,
  MarginCenterCadenceAttentionLifecycle,
  MarginCenterCadenceAttentionResolveBody,
} from '../types/cadenceAttention';

import type {
  MarginCenterComparisonMode,
  MarginCenterTimeGrain,
} from '../types/marginCenter';
import { toSpanishNumber } from '../../../modules/pricingJourney/formatNumber';

const PAGE_SIZE = 25;

const LIFECYCLES: MarginCenterCadenceAttentionLifecycle[] = [
  'active_attention',
  'historical_unresolved',
  'resolved',
];

const COMPARISON_MODES: MarginCenterComparisonMode[] = [
  'previous_period',
  'year_over_year',
  'custom',
  'none',
];

const TIME_GRAINS: MarginCenterTimeGrain[] = [
  'day',
  'week',
  'month',
];

const DISPOSITIONS: Array<{
  value: MarginCenterCadenceAttentionResolveBody['disposition'];
  label: string;
}> = [
  {
    value: 'explained',
    label: 'Situación explicada',
  },
  {
    value: 'not_relevant',
    label: 'No relevante',
  },
  {
    value: 'duplicate',
    label: 'Duplicado',
  },
  {
    value: 'discarded',
    label: 'Descartado',
  },
  {
    value: 'insufficient_evidence',
    label: 'Evidencia insuficiente',
  },
];

function selected<T extends string>(
  raw: string | null,
  allowed: T[],
  fallback: T,
): T {
  return raw && allowed.includes(raw as T)
    ? raw as T
    : fallback;
}

function civilDate(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim() ?? '';

  return /^\d{4}-\d{2}-\d{2}$/.test(normalized)
    ? normalized
    : null;
}

function safeReturnPath(
  path: string | null,
): string {
  return path?.startsWith('/margin-center/')
    && !path.startsWith('//')
    ? path
    : '/margin-center/control-comercial?context=global';
}

function formatDate(
  value: string | null | undefined,
): string {
  if (!value) return '—';

  const dateOnly =
    /^(\d{4}-\d{2}-\d{2})/.exec(value)?.[1];

  if (!dateOnly) return value;

  const [year, month, day] =
    dateOnly.split('-').map(Number);

  const date = new Date(
    year,
    month - 1,
    day,
  );

  return Number.isNaN(date.getTime())
    ? dateOnly
    : date.toLocaleDateString(
      'es-ES',
      { dateStyle: 'medium' },
    );
}

function formatNumber(
  value: number | null | undefined,
  maximumFractionDigits = 1,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return toSpanishNumber(value, { maximumFractionDigits });
}

function lifecycleLabel(
  lifecycle: MarginCenterCadenceAttentionLifecycle,
): string {
  if (lifecycle === 'active_attention') {
    return 'Activos';
  }

  if (lifecycle === 'historical_unresolved') {
    return 'Históricos sin resolver';
  }

  return 'Archivados';
}

function ageBucketLabel(
  value: string | null | undefined,
): string {
  switch (value) {
    case 'under_3_months':
      return '< 3 meses';

    case '3_to_6_months':
      return '3–6 meses';

    case '6_to_12_months':
      return '6–12 meses';

    case '1_to_2_years':
      return '1–2 años';

    case '2_to_5_years':
      return '2–5 años';

    case '5_plus_years':
      return '5+ años';

    default:
      return '—';
  }
}

function dispositionLabel(
  value: string | null | undefined,
): string {
  return DISPOSITIONS.find(
    (item) => item.value === value,
  )?.label ?? 'Resuelto';
}

function SummaryCard({
  label,
  value,
  helper,
  active = false,
  onClick,
}: {
  label: string;
  value: string;
  helper: string;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <Paper
      elevation={0}
      component={
        onClick
          ? 'button'
          : 'div'
      }
      onClick={onClick}
      sx={{
        minWidth: 0,
        minHeight: 76,
        p: 0.9,
        textAlign: 'left',
        borderRadius: 1.6,
        border: active
          ? '1px solid rgba(56,189,248,0.44)'
          : '1px solid rgba(148,163,184,0.14)',
        bgcolor: active
          ? 'rgba(8,47,73,0.20)'
          : 'rgba(15,23,42,0.44)',
        color: 'inherit',
        cursor: onClick
          ? 'pointer'
          : 'default',
        appearance: 'none',
        width: '100%',
        transition:
          'background-color 0.16s ease, border-color 0.16s ease',
        '&:hover': onClick
          ? {
            bgcolor: active
              ? 'rgba(8,47,73,0.28)'
              : 'rgba(30,41,59,0.54)',
            borderColor:
              'rgba(56,189,248,0.28)',
          }
          : undefined,
        '&:focus-visible': onClick
          ? {
            outline: '2px solid #38bdf8',
            outlineOffset: 1,
          }
          : undefined,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: active
            ? '#7dd3fc'
            : '#94a3b8',
          fontWeight: 850,
          fontSize: '0.75rem',
          lineHeight: 1.2,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: '#f8fafc',
          fontSize: '1.4rem',
          fontWeight: 900,
          mt: 0.12,
          lineHeight: 1.05,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
          display: 'block',
          mt: 0.28,
          lineHeight: 1.25,
          fontSize: '0.75rem',
        }}
      >
        {helper}
      </Typography>
    </Paper>
  );
}

export default function MarginCenterCadenceAttentionPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const lifecycle =
    selected(
      params.get('lifecycle'),
      LIFECYCLES,
      'active_attention',
    );

  const cursor =
    params.get('cursor') ?? '';

  const backPath =
    safeReturnPath(
      params.get('returnTo'),
    );

  const selectedRelationshipCustomerId =
    params.get('relationshipCustomer')?.trim()
    || null;

  const selectedRelationshipProductId =
    params.get('relationshipProduct')?.trim()
    || null;

  const selectedRelationshipFollowUpId =
    params.get('relationshipFollowUp')?.trim()
    || null;

  const selectedRelationshipCadenceOccurrence =
    params.get('relationshipCadenceOccurrence')?.trim()
    || null;

  const [
    cursorTrail,
    setCursorTrail,
  ] = useState<string[]>([]);

  const [
    listScroll,
    setListScroll,
  ] = useState(0);

  const [
    managedItem,
    setManagedItem,
  ] = useState<MarginCenterCadenceAttentionItem | null>(
    null,
  );

  const deferred = useDeferredCadenceActions();

  useEffect(() => {
    if (
      !selectedRelationshipCustomerId
      && !selectedRelationshipProductId
      && listScroll > 0
    ) {
      window.scrollTo({
        top: listScroll,
        behavior: 'auto',
      });
    }
  }, [
    listScroll,
    selectedRelationshipCustomerId,
    selectedRelationshipProductId,
  ]);

  const summaryQuery = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'cadence-page',
      'summary',
    ],
    queryFn: () =>
      getMarginCenterCadenceAttention({
        lifecycle: 'active_attention',
        limit: 1,
      }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const listQuery = useQuery({
    queryKey: [
      'margin-center',
      'cadence-attention',
      'cadence-page',
      lifecycle,
      PAGE_SIZE,
      cursor,
    ],
    queryFn: () =>
      getMarginCenterCadenceAttention({
        lifecycle,
        limit: PAGE_SIZE,
        cursor: cursor || undefined,
      }),
    staleTime: 20 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const activeCount =
    deferred.adjustCount('pending', summaryQuery.data?.summary.active_count)
    ?? 0;

  const historicalCount =
    deferred.adjustCount('historical', summaryQuery.data?.summary.historical_unresolved_count)
    ?? 0;

  const resolvedCount =
    deferred.adjustCount('resolved', summaryQuery.data?.summary.resolved_count)
    ?? 0;

  const dataCutoffDate =
    civilDate(
      summaryQuery.data?.data_cutoff_date,
    );

  const requestedComparisonMode =
    selected(
      params.get('comparison'),
      COMPARISON_MODES,
      'year_over_year',
    );

  const requestedComparisonStartDate =
    civilDate(
      params.get('comparisonFrom'),
    );

  const requestedComparisonEndDate =
    civilDate(
      params.get('comparisonTo'),
    );

  const relationshipComparisonMode =
    requestedComparisonMode === 'custom'
    && (
      !requestedComparisonStartDate
      || !requestedComparisonEndDate
    )
      ? 'year_over_year'
      : requestedComparisonMode;

  const relationshipStartDate =
    civilDate(
      params.get('from'),
    )
    ?? (
      dataCutoffDate
        ? `${
          dataCutoffDate.slice(0, 4)
        }-01-01`
        : null
    );

  const relationshipEndDate =
    civilDate(
      params.get('to'),
    )
    ?? dataCutoffDate;

  const relationshipComparisonStartDate =
    relationshipComparisonMode === 'custom'
      ? requestedComparisonStartDate
      : null;

  const relationshipComparisonEndDate =
    relationshipComparisonMode === 'custom'
      ? requestedComparisonEndDate
      : null;

  const relationshipTimeGrain =
    selected(
      params.get('grain'),
      TIME_GRAINS,
      'week',
    );

  const changeLifecycle = (
    nextLifecycle: MarginCenterCadenceAttentionLifecycle,
  ) => {
    const next =
      new URLSearchParams(params);

    next.set(
      'lifecycle',
      nextLifecycle,
    );

    next.delete('cursor');
    next.delete('relationshipCustomer');
    next.delete('relationshipProduct');
    next.delete('relationshipFollowUp');
    next.delete('relationshipCadenceOccurrence');
    next.delete('relationshipReturnTo');

    setCursorTrail([]);
    setManagedItem(null);
    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const nextPage = () => {
    const nextCursor =
      listQuery.data?.page.next_cursor;

    if (!nextCursor) return;

    setCursorTrail((current) => [
      ...current,
      cursor,
    ]);

    const next =
      new URLSearchParams(params);

    next.set(
      'cursor',
      nextCursor,
    );

    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const previousPage = () => {
    if (cursorTrail.length === 0) {
      return;
    }

    const previous =
      cursorTrail[
        cursorTrail.length - 1
      ];

    setCursorTrail((current) =>
      current.slice(0, -1));

    const next =
      new URLSearchParams(params);

    if (previous) {
      next.set(
        'cursor',
        previous,
      );
    } else {
      next.delete('cursor');
    }

    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const firstPage = () => {
    const next =
      new URLSearchParams(params);

    next.delete('cursor');

    setCursorTrail([]);
    setParams(next);

    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const openRelationship = (
    item: MarginCenterCadenceAttentionItem,
  ) => {
    setListScroll(window.scrollY);
    setManagedItem(null);

    const next =
      new URLSearchParams(params);

    next.set(
      'relationshipCustomer',
      item.customer_id,
    );

    next.set(
      'relationshipProduct',
      item.product_id,
    );

    next.set(
      'relationshipCadenceOccurrence',
      item.first_outside_p90_date,
    );

    next.delete('relationshipFollowUp');
    next.delete('relationshipReturnTo');

    setParams(next);
  };

  const selectRelationshipFollowUp = (
    id: string,
  ) => {
    const next =
      new URLSearchParams(params);

    next.set(
      'relationshipFollowUp',
      id,
    );

    setParams(
      next,
      { replace: true },
    );
  };

  const closeRelationshipWorkspace = () => {
    const next =
      new URLSearchParams(params);

    next.delete('relationshipCustomer');
    next.delete('relationshipProduct');
    next.delete('relationshipFollowUp');
    next.delete('relationshipCadenceOccurrence');
    next.delete('relationshipReturnTo');

    setParams(
      next,
      { replace: true },
    );
  };

  const lifecycleTab: CadenceListTab =
    lifecycle === 'historical_unresolved' ? 'historical' : lifecycle === 'resolved' ? 'resolved' : 'pending';

  const managedTarget: CadenceManagementTarget | null = managedItem
    ? {
      customer_id: managedItem.customer_id,
      customer_label: managedItem.customer_label,
      product_id: managedItem.product_id,
      product_label: managedItem.product_label,
      first_outside_p90_date: managedItem.first_outside_p90_date,
      last_purchase_date: managedItem.facts?.last_purchase_date ?? managedItem.history?.last_purchase_date ?? null,
      days_since_last_purchase: managedItem.facts?.days_since_last_purchase ?? null,
      p90_interval_days: managedItem.facts?.p90_interval_days ?? null,
    }
    : null;

  // Filas que se están guardando (con «Deshacer») no se muestran.
  const visibleItems = (listQuery.data?.items ?? []).filter(
    (item) => !deferred.pendingKeys.has(cadenceRowKey(item.customer_id, item.product_id, item.first_outside_p90_date)),
  );

  const selectedCount =
    deferred.adjustCount(lifecycleTab, listQuery.data?.summary.selected_count)
    ?? 0;

  const pageLabel =
    cursor
      ? cursorTrail.length > 0
        ? `Página ${cursorTrail.length + 1}`
        : 'Página actual'
      : 'Página 1';

  return (
    <Box
      sx={pageFrameSx}
    >
      <Stack spacing={0.95}>
        <MarginCenterSpecializedPageHeader
          backLabel={backLabel(backPath)}
          onBack={() =>
            navigate(backPath)}
          eyebrow="Avisos comerciales"
          title="Cadencia"
          description="Relaciones cliente-producto fuera de su patrón histórico de compra. La señal no implica churn, riesgo ni causalidad."
          meta={
            dataCutoffDate
              ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Datos hasta ${
                    formatDate(
                      dataCutoffDate,
                    )
                  }`}
                  sx={{
                    height: 24,
                    '& .MuiChip-label': {
                      px: 0.85,
                      fontSize: '0.75rem',
                      fontWeight: 750,
                    },
                  }}
                />
              )
              : undefined
          }
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gap: 0.75,
          }}
        >
          <SummaryCard
            label="Activos"
            value={
              summaryQuery.isError
                ? '—'
                : summaryQuery.isPending
                  ? '…'
                  : toSpanishNumber(activeCount)
            }
            helper="Rupturas dentro de la ventana operativa."
            active={
              lifecycle === 'active_attention'
            }
            onClick={() =>
              changeLifecycle(
                'active_attention',
              )}
          />

          <SummaryCard
            label="Históricos sin resolver"
            value={
              summaryQuery.isError
                ? '—'
                : summaryQuery.isPending
                  ? '…'
                  : toSpanishNumber(historicalCount)
            }
            helper="Episodios antiguos que siguen sin memoria comercial."
            active={
              lifecycle === 'historical_unresolved'
            }
            onClick={() =>
              changeLifecycle(
                'historical_unresolved',
              )}
          />

          <SummaryCard
            label="Archivados"
            value={
              summaryQuery.isError
                ? '—'
                : summaryQuery.isPending
                  ? '…'
                  : toSpanishNumber(resolvedCount)
            }
            helper="Ocurrencias resueltas sin borrar su historia."
            active={
              lifecycle === 'resolved'
            }
            onClick={() =>
              changeLifecycle(
                'resolved',
              )}
          />

          <SummaryCard
            label="Horizonte observable"
            value={
              dataCutoffDate
                ? formatDate(
                  dataCutoffDate,
                )
                : summaryQuery.isPending
                  ? '…'
                  : 'No disponible'
            }
            helper={
              summaryQuery.data?.data_lag_days
                && summaryQuery.data.data_lag_days > 0
                ? `${
                  summaryQuery.data.data_lag_days
                } días entre el corte y la fecha actual.`
                : 'La señal se evalúa hasta el último dato observable.'
            }
          />
        </Box>

        {summaryQuery.isError && (
          <Alert
            severity="warning"
            action={(
              <Button
                size="small"
                color="inherit"
                onClick={() =>
                  void summaryQuery.refetch()}
              >
                Reintentar
              </Button>
            )}
          >
            No se pudo recuperar el resumen de Cadencia.
            No se muestran falsos ceros.
          </Alert>
        )}

        {summaryQuery.data
          && summaryQuery.data.data_lag_days
          && summaryQuery.data.data_lag_days > 0 && (
          <Alert
            severity="info"
            sx={{
              py: 0.05,
              '& .MuiAlert-icon': {
                py: 0.45,
              },
              '& .MuiAlert-message': {
                py: 0.45,
              },
            }}
          >
            La ausencia de compra se evalúa solo hasta {
              formatDate(
                summaryQuery.data.data_cutoff_date,
              )
            }. El tiempo posterior al corte de datos no
            se interpreta como inactividad.
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: {
              xs: 1.15,
              md: 1.4,
            },
            border:
              '1px solid rgba(148,163,184,0.16)',
            bgcolor:
              'rgba(15,23,42,0.54)',
            borderRadius: 2.25,
          }}
        >
          <Stack spacing={1.1}>
            <Stack
              direction={{
                xs: 'column',
                md: 'row',
              }}
              spacing={0.75}
              justifyContent="space-between"
              alignItems={{
                xs: 'stretch',
                md: 'center',
              }}
            >
              <Stack
                direction="row"
                spacing={0.7}
                useFlexGap
                flexWrap="wrap"
              >
                {LIFECYCLES.map(
                  (candidate) => {
                    const count =
                      candidate === 'active_attention'
                        ? activeCount
                        : candidate === 'historical_unresolved'
                          ? historicalCount
                          : resolvedCount;

                    return (
                      <Button
                        key={candidate}
                        size="small"
                        variant={
                          lifecycle === candidate
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() =>
                          changeLifecycle(
                            candidate,
                          )}
                        sx={{
                          textTransform: 'none',
                        }}
                      >
                        {lifecycleLabel(candidate)}
                        {' · '}
                        {toSpanishNumber(count)}
                      </Button>
                    );
                  },
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                justifyContent="flex-end"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontVariantNumeric:
                      'tabular-nums',
                  }}
                >
                  {pageLabel}
                </Typography>

                <Chip
                  size="small"
                  variant="outlined"
                  label={`${
                    listQuery.data?.page.returned_count ?? 0
                  } de ${
                    toSpanishNumber(selectedCount)
                  }`}
                />
              </Stack>
            </Stack>

            <Divider
              sx={{
                borderColor:
                  'rgba(148,163,184,0.12)',
              }}
            />

            <CadenceManagementDrawer
              open={Boolean(managedItem)}
              target={managedTarget}
              onClose={() => setManagedItem(null)}
              onConfirm={(target, decision) => {
                deferred.schedule(cadenceDecisionAction(target, decision, lifecycleTab));
                setManagedItem(null);
              }}
            />

            {listQuery.isFetching && (
              <Box
                sx={{
                  height: 2,
                  bgcolor:
                    'rgba(56,189,248,0.12)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '35%',
                    height: '100%',
                    bgcolor: '#38bdf8',
                    opacity: 0.8,
                  }}
                />
              </Box>
            )}

            {listQuery.isError && (
              <Alert
                severity="warning"
                action={(
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() =>
                      void listQuery.refetch()}
                  >
                    Reintentar
                  </Button>
                )}
              >
                No se pudo consultar el inventario de Cadencia.
              </Alert>
            )}

            {listQuery.data
              && visibleItems.length === 0 && (
              <Alert severity="info">
                No hay ocurrencias en {
                  lifecycleLabel(
                    lifecycle,
                  ).toLowerCase()
                }.
              </Alert>
            )}

            {listQuery.data
              && visibleItems.length > 0 && (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border:
                    '1px solid rgba(148,163,184,0.13)',
                  borderRadius: 1.75,
                  bgcolor:
                    'rgba(2,6,23,0.20)',
                  overflowX: 'auto',
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 1180,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        'Relación cliente · producto',
                        'Historial observado',
                        'Patrón',
                        'Última compra',
                        'Ruptura',
                        lifecycle === 'resolved'
                          ? 'Resolución'
                          : 'Estado',
                        'Acciones',
                      ].map((label) => (
                        <TableCell
                          key={label}
                          sx={{
                            color: '#64748b',
                            fontWeight: 850,
                            fontSize: '0.74rem',
                            borderColor:
                              'rgba(148,163,184,0.10)',
                            whiteSpace: 'nowrap',
                            py: 0.9,
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleItems.map(
                      (item) => (
                        <TableRow
                          key={item.attention_key}
                          hover
                          sx={{
                            '& td': {
                              borderColor:
                                'rgba(148,163,184,0.09)',
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              minWidth: 230,
                              py: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
                              <Typography variant="body1" sx={{ color: '#f8fafc', fontWeight: 900, lineHeight: 1.15 }}>
                                {item.customer_label}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#cbd5e1', fontWeight: 800 }}>
                                {item.product_label}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 200,
                              py: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#cbd5e1',
                              }}
                            >
                              {formatDate(
                                item.history.first_purchase_date,
                              )}
                              {' → '}
                              {formatDate(
                                item.history.last_purchase_date,
                              )}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                display: 'block',
                                mt: 0.15,
                              }}
                            >
                              {item.history.purchase_year_count}
                              {item.history.purchase_year_count === 1
                                ? ' año con compra'
                                : ' años con compra'}
                              {' · '}
                              {item.history.purchase_days}
                              {' días de compra'}
                            </Typography>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 135,
                              py: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#cbd5e1',
                                fontVariantNumeric:
                                  'tabular-nums',
                              }}
                            >
                              P90 {
                                formatNumber(
                                  item.cadence.p90_interval_days,
                                )
                              } días
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                display: 'block',
                                mt: 0.15,
                              }}
                            >
                              {
                                item.history.completed_interval_count
                              } intervalos completos
                            </Typography>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 145,
                              py: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#cbd5e1',
                              }}
                            >
                              {formatDate(
                                item.history.last_purchase_date,
                              )}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                display: 'block',
                                mt: 0.15,
                              }}
                            >
                              Hace {
                                item.cadence.days_since_last_purchase
                              } días
                            </Typography>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 150,
                              py: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#7dd3fc',
                                fontWeight: 850,
                                fontVariantNumeric:
                                  'tabular-nums',
                              }}
                            >
                              +{
                                formatNumber(
                                  item.cadence.days_over_p90,
                                )
                              } días
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: '#64748b',
                                display: 'block',
                                mt: 0.15,
                              }}
                            >
                              Desde {
                                formatDate(
                                  item.first_outside_p90_date,
                                )
                              }
                              {' · '}
                              {ageBucketLabel(
                                item.cadence.age_bucket,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            sx={{
                              minWidth: 175,
                              py: 1,
                            }}
                          >
                            {lifecycle === 'resolved'
                              ? (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#a7f3d0',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {dispositionLabel(
                                      item.resolution?.disposition,
                                    )}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      display: 'block',
                                      mt: 0.15,
                                      maxWidth: 240,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {
                                      item.resolution
                                        ?.resolution_type
                                      || item.resolution
                                        ?.conclusion
                                      || formatDate(
                                        item.resolution
                                          ?.resolved_at,
                                      )
                                    }
                                  </Typography>
                                </>
                              )
                              : (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        lifecycle
                                          === 'active_attention'
                                          ? '#7dd3fc'
                                          : '#fbbf24',
                                      fontWeight: 850,
                                    }}
                                  >
                                    {lifecycleLabel(
                                      lifecycle,
                                    )}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#64748b',
                                      display: 'block',
                                      mt: 0.15,
                                    }}
                                  >
                                    Episodio vigente sin memoria
                                    comercial de resolución
                                  </Typography>
                                </>
                              )}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              minWidth: 205,
                              py: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.6}
                              justifyContent="flex-end"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                  openRelationship(
                                    item,
                                  )}
                                sx={{
                                  textTransform: 'none',
                                }}
                              >
                                Abrir relación
                              </Button>

                              {lifecycle !== 'resolved' && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() =>
                                    setManagedItem(
                                      item,
                                    )}
                                  sx={{
                                    textTransform: 'none',
                                    color: '#fbbf24',
                                  }}
                                >
                                  Gestionar
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {listQuery.data && (
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={0.75}
                alignItems={{
                  xs: 'stretch',
                  sm: 'center',
                }}
                justifyContent="space-between"
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#64748b',
                    fontVariantNumeric:
                      'tabular-nums',
                  }}
                >
                  Mostrando {
                    listQuery.data.page.returned_count
                  } de {
                    toSpanishNumber(selectedCount)
                  } · {
                    lifecycleLabel(lifecycle)
                  }
                </Typography>

                <Stack
                  direction="row"
                  spacing={0.75}
                  justifyContent="flex-end"
                >
                  {cursor
                    && cursorTrail.length === 0 && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={firstPage}
                    >
                      Inicio
                    </Button>
                  )}

                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      cursorTrail.length === 0
                    }
                    onClick={previousPage}
                  >
                    Anterior
                  </Button>

                  <Button
                    size="small"
                    variant="contained"
                    disabled={
                      !listQuery.data.page.has_more
                      || !listQuery.data.page.next_cursor
                    }
                    onClick={nextPage}
                  >
                    Siguiente
                  </Button>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>

        {selectedRelationshipCustomerId
          && selectedRelationshipProductId
          && (
          <CustomerRelationshipWorkspace
            customerId={
              selectedRelationshipCustomerId
            }
            productId={
              selectedRelationshipProductId
            }
            startDate={
              relationshipStartDate
            }
            endDate={
              relationshipEndDate
            }
            comparisonMode={
              relationshipComparisonMode
            }
            comparisonStartDate={
              relationshipComparisonStartDate
            }
            comparisonEndDate={
              relationshipComparisonEndDate
            }
            timeGrain={
              relationshipTimeGrain
            }
            initialMode={
              selectedRelationshipCadenceOccurrence
                ? 'follow_up'
                : 'analysis'
            }
            initialFollowUpId={
              selectedRelationshipFollowUpId
            }
            cadenceAttentionOccurrenceDate={
              selectedRelationshipCadenceOccurrence
            }
            onFollowUpIdChange={
              selectRelationshipFollowUp
            }
            onClose={
              closeRelationshipWorkspace
            }
          />
        )}
      </Stack>
    </Box>
  );
}
